import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Empty, Message, Radio, Spin, Tag, Typography } from "@arco-design/web-react";
import { IconEmail, IconRefresh } from "@arco-design/web-react/icon";
import FlowGraph from "./FlowGraph";
import { FetchApi, PostApi } from "../../Api/AuthenticatedApi";
import {
  BROWSER_FRAME, absoluteSrc, addNode, popTo, arriveUrl, buttonLabels, clearDriveState, clearFormState, confirmLinkIn, confirmSteps, currentNode,
  driveEmailHtml, emptyTree, fillForm, flowGraph, formScriptUrl, graphStatus, loadDriveState, momentFor, pathOf, quotedSubject,
  readFormState, runOutSteps, sampleSignup, saveDriveState, shownSubject, standInPage, testBase, theirRewardLink, treeFromMoments, updateNode,
  writeFormState,
} from "./driveHelpers";

const { Text, Title } = Typography;

// Test drive: try the campaign's whole signup flow with nothing saved, as the
// subscriber would. On top, the flow as a graph (flowGraph: every way through
// it, from the campaign's settings, including the decision where they hold a
// reward link: they open it, or it runs out first). Below, their browser and
// their inbox: their browser starts on a stand-in website with the real form
// (test mode); the signup email arrives in their inbox, and the links in it
// (Confirm, the reward, tracked links) open in their browser, with an address
// bar showing where they went. Runs on a test session (POST
// /api/campaigns/:id/test-drive); the pages report back with postMessage.
//
// Each thing that happens is a moment, with a snapshot of both panes, landing
// on a step of the graph. The graph lights up as they go: the current step,
// and the steps done. Clicking a step they've done goes back to it like
// popping a stack: what came after is cleared, both panes are as they were,
// and their test reward link is put back as it was then (fresh, or run out),
// so what they do next is real. Clicking the next step does it for them
// where it can (open the email, click Confirm, click the reward, let the link
// run out). Under the graph, the current step's details: what happened, and
// what's worth fixing.
//
// Closing the panel keeps everything, and it's saved for this browser tab, so
// they can fix something and come back to where they were; reopening reloads
// their browser (on the stand-in, it shows the latest saved form). Reset
// starts clean — and it happens by itself on reopening when the campaign's
// settings have changed since the drive started (the server's settings_key).

const KIND_COLOR = { done: "rgb(var(--green-6))", info: "rgb(var(--arcoblue-6))", warn: "rgb(var(--orange-6))", tip: "rgb(var(--purple-6))" };

// Where a warning's fix button takes them (TestStep.fix from the backend).
const FIX_LABELS = {
  settings: "Fix in Signup Settings",
  signup_email: "Fix in Emails",
  forms: "Fix in Web Forms",
  contact: "Open the contact",
};

// The saved drive's shape; an older one starts over.
const SAVE_VERSION = 3;

const EMPTY_SNAP = { browser: { src: "", address: "" }, form: {}, emailIds: [], unread: [], openId: null, rewardUrl: "", ranOut: "" };

const lastSnapIndex = (moments) => {
  for (let i = moments.length - 1; i >= 0; i--) if (moments[i].snap) return i;
  return -1;
};

const isRewardClick = (href) => /\/arrive\?reward=/.test(href || "");

// It's the campaign page's Test mode: shown in place of the settings while
// visible, and kept (with its state) while the page is in Edit mode.
export default function TestDrive({ campaignId, visible, onFix }) {
  const { getAccessTokenSilently } = useAuth0();
  const [drive, setDrive] = React.useState(null);
  const [error, setError] = React.useState("");
  const [device, setDevice] = React.useState("desktop");
  const [tree, setTree] = React.useState(emptyTree); // every path tried (see pathOf)
  const moments = React.useMemo(() => pathOf(tree), [tree]); // the current path
  const [mail, setMail] = React.useState({}); // every email that arrived, by id
  // Their browser frame loads browserMount.src ("" = the stand-in website)
  // when its key changes; clicks and redirects then move it along by
  // themselves.
  const [browserMount, setBrowserMount] = React.useState({ key: 0, src: "" });
  const [pendingAddress, setPendingAddress] = React.useState(""); // "Loading…" between a click and where it lands
  const [busy, setBusy] = React.useState("");
  const startedAt = React.useRef(Date.now());
  const siteFrame = React.useRef(null);
  const emailFrame = React.useRef(null);
  const lastSignupEmail = React.useRef("");
  const pendingClick = React.useRef(null); // the link just clicked: { label, href }
  const momentsRef = React.useRef([]);
  momentsRef.current = moments;
  const openedBefore = React.useRef(false);

  const base = drive ? testBase(drive.public_base_url, drive.org_hash, drive.session_id) : "";
  // The graph names the email's buttons in their own words, once it's here.
  const labels = React.useMemo(() => (drive ? buttonLabels(Object.values(mail), base, drive.org_hash) : {}), [drive, mail, base]);
  // Why the signup email can't go out, if it can't: what the last signup on
  // this path found, else what the server says of the settings now.
  const blocked = React.useMemo(() => {
    for (let i = moments.length - 1; i >= 0; i--) {
      if (moments[i].kind === "signup") return moments[i].blocked || null;
    }
    return (drive && drive.email_problem) || null;
  }, [moments, drive]);
  const graph = React.useMemo(() => flowGraph(drive, labels, blocked), [drive, labels, blocked]);
  const lastIdx = lastSnapIndex(moments);
  const shown = moments[lastIdx];
  const snap = (shown && shown.snap) || EMPTY_SNAP;

  // addMoment records a moment on the current path: its snapshot is the last
  // one changed by patch (an object, or a function of the last snapshot);
  // node is the graph step it lands on (null: off the graph).
  const addMoment = React.useCallback((kind, title, { node = null, detail, steps, patch, stage, awaitingNav, blocked } = {}) => {
    setTree((t) => {
      const list = pathOf(t);
      const prev = (list[lastSnapIndex(list)] || {}).snap || EMPTY_SNAP;
      const change = typeof patch === "function" ? patch(prev) : patch || {};
      const at = Date.now() - startedAt.current;
      return addNode(t, {
        id: `${Date.now()}-${Math.random()}`, kind, node, title, detail, stage, awaitingNav, blocked, at,
        steps: (steps || []).map((s) => ({ ...s, at })), snap: { ...prev, ...change },
      });
    });
  }, []);

  // mountBrowser loads a snapshot's page in their browser, with the form's
  // own state as it was then.
  const mountBrowser = React.useCallback((s, sessionId) => {
    if (sessionId) writeFormState(window.localStorage, sessionId, s.form);
    setPendingAddress("");
    setBrowserMount((b) => ({ key: b.key + 1, src: s.browser.src }));
  }, []);

  // arrive delivers an email to the inbox (after a moment, like real mail);
  // stage is the stage it belongs to (email or followup). It isn't opened:
  // they open it (a moment of its own).
  const arrive = React.useCallback((email, stage) => {
    setTimeout(() => {
      const id = `${Date.now()}-${Math.random()}`;
      setMail((m) => ({ ...m, [id]: { ...email, id, stage } }));
      addMoment("arrived", `${quotedSubject(email, "An")} arrived in their inbox`, {
        node: stage === "followup" ? "followup" : "email",
        stage,
        detail: `From ${email.from}`,
        patch: (s) => ({
          emailIds: [id, ...s.emailIds],
          unread: [...s.unread, id],
          // A new email with the reward carries a fresh reward link.
          ranOut: (email.html || "").includes("/incentive/") ? "" : s.ranOut,
        }),
      });
    }, 900);
  }, [addMoment]);

  // start begins a new drive.
  const start = React.useCallback(() => {
    setDrive(null);
    setError("");
    setTree(emptyTree());
    setMail({});
    setPendingAddress("");
    setBrowserMount((b) => ({ key: b.key + 1, src: "" }));
    lastSignupEmail.current = "";
    pendingClick.current = null;
    startedAt.current = Date.now();
    PostApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/test-drive`,
      (data) => {
        setDrive(data);
        setTree(treeFromMoments([
          { id: "visit", kind: "visit", node: "visit", title: "They visit your website", detail: "A stand-in page, with your form", at: 0, steps: [], snap: EMPTY_SNAP },
        ]));
      },
      () => {},
      (err) => setError((err && err.message) || "Couldn't start the test drive"),
      {});
  }, [campaignId, getAccessTokenSilently]);

  const reset = React.useCallback((sessionId) => {
    if (sessionId) clearFormState(window.localStorage, sessionId);
    clearDriveState(window.sessionStorage, campaignId);
    start();
  }, [campaignId, start]);

  // resetIfSettingsChanged starts over when the campaign's settings no longer
  // match the drive's.
  const resetIfSettingsChanged = React.useCallback((d) => {
    FetchApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/test-drive/settings-key`,
      (data) => {
        if (data && data.settings_key && data.settings_key !== d.settings_key) {
          reset(d.session_id);
          Message.info("Your settings changed, so Test drive started over with them.");
        } else if (data) {
          // The same settings, but the flow may have changed since (a form or
          // email added, a sender verified): take it as it is now, keeping
          // the session.
          // eslint-disable-next-line no-unused-vars
          const { session_id: _s, expires_at: _e, org_hash: _o, public_base_url: _p, ...flow } = data;
          setDrive((cur) => (cur ? { ...cur, ...flow, email_problem: flow.email_problem || null } : cur));
        }
      },
      () => {},
      () => {});
  }, [campaignId, getAccessTokenSilently, reset]);

  React.useEffect(() => {
    if (!visible) return;
    if (drive) {
      if (openedBefore.current) {
        // Their browser reloads (on the stand-in, it shows the latest saved
        // form, which may have just been fixed).
        const list = momentsRef.current;
        const last = list[lastSnapIndex(list)];
        if (last) mountBrowser(last.snap, drive.session_id);
        resetIfSettingsChanged(drive);
      }
    } else {
      const saved = loadDriveState(window.sessionStorage, campaignId);
      if (saved && saved.v === SAVE_VERSION && saved.tree) {
        const savedPath = pathOf(saved.tree);
        setDrive(saved.drive);
        setTree(saved.tree);
        setMail(saved.mail || {});
        setDevice(saved.device || "desktop");
        lastSignupEmail.current = saved.lastSignupEmail || "";
        startedAt.current = saved.startedAt || Date.now();
        const last = savedPath[lastSnapIndex(savedPath)];
        if (last) mountBrowser(last.snap, saved.drive.session_id);
        resetIfSettingsChanged(saved.drive);
      } else {
        start();
      }
    }
    openedBefore.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Keep the drive for this tab.
  React.useEffect(() => {
    if (!drive) return;
    saveDriveState(window.sessionStorage, campaignId, {
      v: SAVE_VERSION, drive, tree, mail, device, lastSignupEmail: lastSignupEmail.current, startedAt: startedAt.current,
    });
  }, [campaignId, drive, tree, mail, device]);

  const limits = drive && drive.reward_limits;
  const emailsOf = (s) => s.emailIds.map((id) => mail[id]).filter(Boolean);
  const heldLinkOf = (s) => (drive ? theirRewardLink(emailsOf(s), s.rewardUrl, drive.org_hash) : "");
  const runOutUrl = `/api/campaigns/${campaignId}/test-drive/${drive ? drive.session_id : ""}/run-out-reward`;

  // syncReward puts their test reward link back as it was at a moment (fresh,
  // or run out): the server only knows its latest state. Resolves once done.
  // (Only needed once a link has been run out.)
  const syncReward = (s, ranOutBefore) => new Promise((resolve) => {
    const link = heldLinkOf(s);
    if (!link || !ranOutBefore) {
      resolve();
      return;
    }
    PostApi(getAccessTokenSilently, runOutUrl,
      () => resolve(),
      () => resolve(),
      () => {
        Message.warning("Couldn't put their reward link back as it was.");
        resolve();
      },
      { reward_url: link, how: s.ranOut || "restore" });
  });

  const ranOut = () => Object.values(tree.nodes).some((n) => n.kind === "runout");

  // goTo goes back to a moment like popping a stack: everything after it
  // goes, and both panes and their reward link are as they were then, so what
  // they do next is real.
  const goTo = (m) => {
    const before = ranOut();
    setTree((t) => popTo(t, m.id));
    mountBrowser(m.snap, drive.session_id);
    return syncReward(m.snap, before);
  };

  // openEmail opens an email in their inbox. The first time it's the flow's
  // "Open it" step; opening it again later is a revisit (a note, off the
  // graph), so going back to "Open it" shows the inbox as it was then.
  const openEmail = (id) => {
    if (id === snap.openId) return;
    const m = mail[id];
    const again = moments.some((x) => x.kind === "opened" && x.snap && x.snap.openId === id);
    addMoment("opened", again ? `They went back to ${quotedSubject(m)}` : `They opened ${quotedSubject(m)}`, {
      node: again ? null : m.stage === "followup" ? "openFollowup" : "open",
      patch: (s) => ({ openId: id, unread: s.unread.filter((x) => x !== id) }),
    });
  };

  // clickInBrowser opens a link in their browser as their click would.
  const clickInBrowser = (href, label) => {
    pendingClick.current = { label, href };
    setPendingAddress("Loading…");
    setBrowserMount((b) => ({ key: b.key + 1, src: href }));
  };

  // runOut makes their (test) reward link dead the way its limits would —
  // time up or downloads used up — then shows what opening it gets them.
  const runOut = (how, s) => {
    const link = heldLinkOf(s);
    if (!link) return;
    setBusy(how);
    PostApi(getAccessTokenSilently, runOutUrl,
      () => {
        setBusy("");
        const [first, ...rest] = runOutSteps(how, limits);
        const browser = { src: `${arriveUrl(base, link)}&replay=1`, address: "Their reward link (run out)" };
        addMoment("runout", first.text.split(":")[0], { node: how, steps: rest, patch: { ranOut: how, browser } });
        mountBrowser({ ...s, browser }, "");
      },
      () => setBusy(""),
      (err) => {
        setBusy("");
        Message.error(`Couldn't run out the reward link (${(err && err.message) || "error"}).`);
      },
      { reward_url: link, how });
  };

  // subscribeForThem fills in the form in their browser (a fresh test
  // address and a name, and anything else it requires) and presses its
  // button, once the form has loaded (it comes in by its own script).
  const subscribeForThem = () => {
    const who = sampleSignup();
    setBusy("subscribe");
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      let doc = null;
      try {
        doc = siteFrame.current && siteFrame.current.contentDocument;
      } catch (e) {
        doc = null; // not the stand-in page
      }
      if (fillForm(doc, who)) {
        clearInterval(timer);
        setBusy("");
        Message.info(`Filled in the form as ${who.email}, and pressed its button.`);
      } else if (tries > 50) {
        clearInterval(timer);
        setBusy("");
        Message.warning("The form didn't load: fill it in in their browser below, and press its button.");
      }
    }, 200);
  };

  // act does a step of the graph for them, from the moment before it (s is
  // that moment's snapshot). Some steps are theirs to do (filling in the
  // form) or happen by themselves (emails arriving).
  const act = (key, s) => {
    const emails = emailsOf(s);
    const find = (stage) => emails.find((m) => (m.stage || "email") === stage);
    switch (key) {
      case "subscribe":
        subscribeForThem();
        return;
      case "email":
      case "followup":
        Message.info("It arrives by itself, after the step before it.");
        return;
      case "rewardOpens":
        Message.info("It opens by itself, after they click Confirm.");
        return;
      case "open":
      case "openFollowup": {
        const m = find(key === "open" ? "email" : "followup");
        if (m) openEmail(m.id);
        return;
      }
      case "confirm": {
        const m = find("email");
        const link = m ? confirmLinkIn(driveEmailHtml(m.html, base), base) : "";
        if (link) clickInBrowser(link, "Confirm");
        else Message.warning("The email has no Confirm button to click.");
        return;
      }
      case "reward": {
        const link = heldLinkOf(s);
        if (link) clickInBrowser(arriveUrl(base, link), "their reward");
        return;
      }
      case "time":
      case "uses":
        runOut(key, s);
        return;
      case "unlimited":
        Message.info("Unlimited is on, so their link never runs out. Switch it off (the reward's Limits) to try that.");
        return;
      case "revisit":
        // Their browser comes back to the page: the form, remembering them.
        addMoment("revisit", "They come back to your site", {
          node: "revisit",
          steps: [{ kind: "info", text: "Your form remembers them in this browser, so it shows its thank-you state instead of asking again." }],
          patch: { browser: { src: "", address: "" } },
        });
        mountBrowser({ ...s, browser: { src: "", address: "" } }, drive.session_id);
        return;
      case "noConfirm":
        addMoment("noconfirm", "They never click Confirm", {
          node: "noConfirm",
          steps: [
            { kind: "info", text: "They stay pending on the campaign's lists: not subscribed, and nothing more is sent to them." },
            { kind: "tip", fix: "forms", fix_field: "forms", text: "People often miss the email. Make sure your form's thank-you message tells them to check their inbox and confirm." },
          ],
        });
        return;
      case "unsub":
      case "unsubFollowup": {
        const m = find(key === "unsub" ? "email" : "followup");
        const link = m ? linkIn(m, (h) => h.startsWith(`${base}/unsubscribe`)) : "";
        if (link) clickInBrowser(link, "Unsubscribe");
        else Message.warning("The email has no Unsubscribe link to click.");
        return;
      }
      default:
        if (key.startsWith("link:")) {
          const name = key.slice(5);
          const m = find("email");
          const link = m ? linkIn(m, (h) => h.startsWith(`${base}/click/${name}`)) : "";
          if (link) clickInBrowser(link, labels.links && labels.links[name] ? labels.links[name] : name);
          else Message.info("That link shows up once the email has arrived.");
        }
    }
  };

  // linkIn finds a link in an email (as it's shown in their inbox, where it
  // opens in their browser).
  const linkIn = (m, match) => {
    const doc = new DOMParser().parseFromString(driveEmailHtml(m.html, base), "text/html");
    const a = Array.from(doc.querySelectorAll("a[href]")).find((el) => match(el.getAttribute("href")));
    return a ? a.getAttribute("href") : "";
  };

  // onNode: a step of the graph was clicked. Reached: go back to it. The next
  // step from one reached: go to that one, then do it.
  const onNode = (key) => {
    const target = graph.find((n) => n.key === key);
    if (target && (target.blocked || target.unreachable)) {
      Message.warning(target.blocked || `Out of reach: ${target.unreachable}`);
      return;
    }
    const reached = momentFor(tree, key);
    if (reached) {
      if (reached.id !== (shown && shown.id)) goTo(reached);
      return;
    }
    const step = graph.find((n) => n.key === key);
    const from = step && step.parent && momentFor(tree, step.parent);
    if (!from) {
      const before = step && graph.find((n) => n.key === step.parent);
      Message.info(`Get to “${before ? before.label : "the step before"}” first.`);
      return;
    }
    const ready = from.id === (shown && shown.id) ? syncReward(from.snap, ranOut()) : goTo(from);
    ready.then(() => act(key, from.snap));
  };

  // Messages from their browser (the stand-in page's form, the confirm page,
  // pages it goes on to). Only our own frames are listened to.
  React.useEffect(() => {
    if (!visible || !drive) return undefined;
    const onMessage = (e) => {
      const msg = e.data;
      if (!msg || msg.type !== "jc-testdrive") return;
      const fromSite = siteFrame.current && e.source === siteFrame.current.contentWindow;
      const fromInbox = emailFrame.current && e.source === emailFrame.current.contentWindow;
      if (!fromSite && !fromInbox) return;
      const click = pendingClick.current;

      if (msg.event === "signup") {
        const res = msg.data || {};
        // The form saves its thank-you state as it reports; snapshot after.
        setTimeout(() => {
          addMoment("signup", res.email ? `They subscribed as ${res.email.to}` : "They pressed Subscribe", {
            node: "subscribe",
            blocked: res.email_blocked || null,
            steps: res.steps || [],
            patch: { form: readFormState(window.localStorage, drive.session_id), browser: { src: "", address: "" } },
          });
          if (res.email) {
            lastSignupEmail.current = res.email.to;
            arrive(res.email, "email");
          }
        }, 150);
      } else if (msg.event === "signup-failed") {
        const status = msg.data && msg.data.status;
        addMoment("signup-failed", "They pressed Subscribe, and the form turned them away", {
          steps: [{
            kind: "warn",
            text: status === 400
              ? "The form turned that email address away as invalid, just as it would on your site."
              : status === 429
                ? "They'd be asked to wait a few minutes before the email is sent again."
                : `The form couldn't be submitted (${status || "network error"}).`,
          }],
          patch: { form: readFormState(window.localStorage, drive.session_id) },
        });
      } else if (msg.event === "navigated") {
        const data = msg.data || {};
        const browser = { src: absoluteSrc(drive.public_base_url, data.replay || data.url), address: data.url || "" };
        pendingClick.current = null;
        setPendingAddress("");
        const list = momentsRef.current;
        const last = list[lastSnapIndex(list)];
        // A file downloads: their browser stays on the page it was on (its
        // address), and the download shows.
        const file = data.download;
        const where = file ? `⬇ ${file}` : `→ ${data.url}`;
        const downloaded = file ? [{ kind: "done", text: `Their browser downloads “${file}”, and stays on the page they were on.` }] : [];
        if (last && last.awaitingNav) {
          // The confirm click went on here: that's where it landed.
          const address = file ? "The tab they clicked Confirm in (downloading)" : data.url || "";
          setTree((t) => updateNode(t, last.id, {
            awaitingNav: false, detail: where, steps: [...(last.steps || []), ...downloaded], snap: { ...last.snap, browser: { ...browser, address } },
          }));
        } else {
          const reward = !click || isRewardClick(click.href);
          const tracked = click && click.href && click.href.startsWith(`${base}/click/`)
            ? click.href.slice(`${base}/click/`.length).split(/[?#/]/)[0] : "";
          const title = click ? `They clicked “${click.label}”` : file ? "Their download started" : "Their reward opened";
          // A tracked link in a campaign email confirms them too, but hands
          // over nothing: the reward only comes with the Confirm button.
          const trackedSteps = tracked && drive.double_opt_in
            ? [
              { kind: "done", text: "Clicking a link in the email confirms them too: they're subscribed now." },
              ...(drive.include_incentive ? [{
                kind: "tip", fix: "signup_email", fix_field: "signup_emails",
                text: "They're subscribed, but got no reward: only the Confirm button hands it over. If this link is the one people click, consider pointing them at the Confirm button instead.",
              }] : []),
            ]
            : [];
          addMoment("went", title, {
            node: tracked ? `link:${tracked}` : click ? (reward ? "reward" : null) : "rewardOpens",
            detail: where,
            steps: [...downloaded, ...trackedSteps],
            patch: (s) => ({ browser: { ...browser, address: file ? s.browser.address : browser.address } }),
          });
        }
      } else if (msg.event === "unsubscribed") {
        pendingClick.current = null;
        setPendingAddress("");
        const data = msg.data || {};
        const list = momentsRef.current;
        const now = list[lastSnapIndex(list)].snap;
        const from = now.openId && mail[now.openId];
        addMoment("unsubscribed", "They clicked Unsubscribe", {
          node: from && from.stage === "followup" ? "unsubFollowup" : "unsub",
          detail: data.url ? `→ ${data.url}` : "",
          steps: [{
            kind: "done",
            text: data.url
              ? "They're sent to your organization's unsubscribe page (nothing was changed)."
              : "They see “You're unsubscribed” (nothing was changed).",
          }],
          patch: {
            browser: data.url
              ? { src: data.url, address: data.url }
              : { src: click ? click.href.replace("drive=1", "replay=1") : "", address: "Your “You're unsubscribed” page" },
          },
        });
      } else if (msg.event === "confirmed") {
        const data = msg.data || {};
        pendingClick.current = null;
        setPendingAddress("");
        const list = momentsRef.current;
        const now = list[lastSnapIndex(list)].snap;
        const signup = now.emailIds.map((id) => mail[id]).find((m) => m && m.stage === "email");
        const href = (click && click.href) || (signup ? confirmLinkIn(driveEmailHtml(signup.html, base), base) : "");
        const replay = href ? href.replace("drive=1", "replay=1") : "";
        const redirects = !!data.url && !data.reward_page;
        addMoment("confirm", "They clicked Confirm", {
          node: "confirm",
          steps: confirmSteps(data),
          awaitingNav: redirects,
          patch: (s) => ({
            browser: redirects
              ? { src: replay, address: "" }
              : { src: replay, address: data.reward_page ? "Your “You're confirmed!” page (then their reward opens)" : "Your “You're confirmed!” page" },
            // A fresh reward link (confirming again mints a new one). With the
            // follow-up, theirs is the one in the follow-up email instead.
            rewardUrl: data.reward_url || s.rewardUrl,
            ranOut: data.reward_url && !data.followup ? "" : s.ranOut,
          }),
        });
        // Confirming again doesn't send the follow-up twice (unless its link
        // was run out: then it brings a fresh one to try again with).
        const hasFollowup = now.emailIds.some((id) => mail[id] && mail[id].stage === "followup");
        if (data.followup && base && (now.ranOut || !hasFollowup)) {
          fetch(`${base}/followup-email?email=${encodeURIComponent(lastSignupEmail.current || "")}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((email) => arrive(email, "followup"))
            .catch(() => Message.warning("The follow-up email couldn't be rendered. Check the reward's follow-up email."));
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [visible, drive, base, mail, addMoment, arrive]);

  // Their clicks on the email's links (which open in their browser): which
  // link, for the graph, and "Loading…" in the address bar meanwhile.
  const watchEmailClicks = (e) => {
    const doc = e.target.contentDocument;
    if (!doc) return;
    doc.addEventListener("click", (ev) => {
      const a = ev.target && ev.target.closest && ev.target.closest(`a[target="${BROWSER_FRAME}"]`);
      if (!a) return;
      pendingClick.current = { label: (a.textContent || "").replace(/\s+/g, " ").trim() || "a link", href: a.getAttribute("href") };
      setPendingAddress("Loading…");
    });
  };

  const emailsShown = emailsOf(snap);
  const openedEmail = snap.openId ? mail[snap.openId] : null;
  const forms = (drive && drive.forms) || [];
  const status = graphStatus(graph, tree);
  const current = currentNode(graph, moments);
  const inInbox = !!shown && (shown.kind === "arrived" || shown.kind === "opened");
  // Off the graph (unsubscribing, a tracked link, a turned-away signup): the
  // moments since the current step, noted under it.
  const extras = [];
  for (let i = lastIdx; i >= 0 && moments[i] && !moments[i].node; i--) if (moments[i].snap) extras.unshift(moments[i]);
  const currentMoment = (() => {
    for (let i = lastIdx; i >= 0; i--) if (moments[i] && moments[i].node) return moments[i];
    return null;
  })();

  return (
    <div style={{ display: visible ? "block" : "none" }}>
      {error && <Alert type="error" content={error} style={{ marginBottom: 12 }} />}
      {!drive && !error && <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>}
      {drive && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <Radio.Group type="button" value={device} onChange={setDevice}
              options={[{ label: "Desktop", value: "desktop" }, { label: "Phone", value: "phone" }]} />
            <Button icon={<IconRefresh />} onClick={() => reset(drive.session_id)}>Reset</Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Uses the campaign's saved settings. Nothing is saved: no contacts, no emails sent, no views or signups counted.
            </Text>
          </div>

          <FlowGraph graph={graph} status={status} current={current} busy={busy} onNode={onNode} onFix={onFix} />

          <Details moments={[currentMoment, ...extras].filter(Boolean)} onFix={onFix} />

          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "stretch" }}>
            {/* Their browser */}
            <Pane title="Their browser" active={!inInbox} style={{ flex: "1.2 1 420px" }}>
              <DeviceFrame phone={device === "phone"}>
                <AddressBar address={pendingAddress || snap.browser.address} />
                {forms.length === 0 && !browserMount.src ? (
                  <Empty
                    style={{ paddingTop: 80 }}
                    description={(
                      <div>
                        <div>This campaign has no web form yet, so there's nothing on your site to sign up with.</div>
                        {onFix && (
                          <Button type="primary" size="small" style={{ marginTop: 10 }} onClick={() => onFix({ fix: "forms", fix_field: "forms" })}>
                            Add one under Web Forms
                          </Button>
                        )}
                      </div>
                    )}
                  />
                ) : (
                <iframe
                  key={`${drive.session_id}-${browserMount.key}`}
                  ref={siteFrame}
                  name={BROWSER_FRAME}
                  title="Their browser"
                  {...(browserMount.src ? { src: browserMount.src } : { srcDoc: standInPage(formScriptUrl(base, "")) })}
                  style={{ width: "100%", flex: 1, minHeight: 520, border: 0, background: "#fff", display: "block" }}
                />
                )}
              </DeviceFrame>
            </Pane>

            {/* Their inbox */}
            <Pane title="Their inbox (simulated)" active={inInbox} style={{ flex: "1 1 360px" }}>
              <DeviceFrame phone={device === "phone"}>
                <TestStrip>TEST · Simulated inbox: nothing was really sent</TestStrip>
                <style>{"@keyframes jc-td-arrive { 0%, 25% { background: rgb(var(--arcoblue-3)); } 100% { background: rgb(var(--arcoblue-1)); } }"}</style>
                {emailsShown.length === 0 ? (
                  <Empty icon={<IconEmail style={{ fontSize: 36 }} />} description="Submit the form to see the signup email arrive here." style={{ paddingTop: 80 }} />
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <div style={{ borderBottom: "1px solid var(--color-border-2)", maxHeight: 132, overflowY: "auto" }}>
                      {emailsShown.map((m) => {
                        // Unread emails stand out, as in a mail app (a bar, a
                        // dot, "New"); one that just arrived flashes once.
                        const unread = snap.unread.includes(m.id);
                        const justArrived = shown && shown.kind === "arrived" && snap.emailIds[0] === m.id;
                        return (
                          <div key={`${m.id}-${justArrived ? shown.id : ""}`}
                            data-unread={unread ? "true" : undefined}
                            onClick={() => openEmail(m.id)}
                            style={{
                              padding: "9px 12px 9px 9px", cursor: "pointer", display: "flex", gap: 8, alignItems: "center",
                              borderLeft: `3px solid ${unread ? "rgb(var(--arcoblue-6))" : "transparent"}`,
                              borderBottom: "1px solid var(--color-border-1)",
                              background: m.id === snap.openId ? "var(--color-fill-2)" : unread ? "rgb(var(--arcoblue-1))" : "transparent",
                              animation: justArrived ? "jc-td-arrive 1.6s ease-out" : undefined,
                            }}>
                            <span aria-hidden style={{ flex: "none", width: 8, height: 8, borderRadius: 4, background: unread ? "rgb(var(--arcoblue-6))" : "transparent" }} />
                            <Text style={{ fontWeight: unread ? 700 : 400, color: unread ? "var(--color-text-1)" : "var(--color-text-2)", flex: "none", maxWidth: device === "phone" ? 100 : 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.from}</Text>
                            <Text style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: unread ? 700 : 400, color: unread ? "var(--color-text-1)" : "var(--color-text-2)", fontStyle: (m.subject || "").trim() ? "normal" : "italic" }}>{shownSubject(m)}</Text>
                            {unread && <Tag size="small" color="arcoblue" style={{ flex: "none" }}>New</Tag>}
                          </div>
                        );
                      })}
                    </div>
                    {openedEmail ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--color-border-1)", fontSize: 13 }}>
                          <Title heading={6} style={{ margin: 0, minWidth: 0 }}>{shownSubject(openedEmail)}</Title>
                          <Text type="secondary">From {openedEmail.from} · To {openedEmail.to}</Text>
                        </div>
                        <iframe
                          key={`${openedEmail.id}-${shown ? shown.id : ""}`}
                          ref={emailFrame}
                          title="Email"
                          srcDoc={driveEmailHtml(openedEmail.html, base)}
                          onLoad={watchEmailClicks}
                          style={{ flex: 1, width: "100%", minHeight: 400, border: 0, background: "#fff" }}
                        />
                      </div>
                    ) : (
                      <Text type="secondary" style={{ padding: 16, textAlign: "center" }}>Click an email to open it.</Text>
                    )}
                  </div>
                )}
              </DeviceFrame>
            </Pane>
          </div>
        </div>
      )}
    </div>
  );
}

// Details: what happened at the current step, and what's worth fixing, plus
// anything since that's off the graph (unsubscribing, a tracked link).
function Details({ moments, onFix }) {
  if (!moments.length) return null;
  return (
    <div style={{ border: "1px solid var(--color-border-2)", borderRadius: 8, padding: "8px 12px", background: "var(--color-bg-2)" }}>
      {moments.map((m) => (
        <div key={m.id} style={{ marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {m.title}
            {m.detail && <span style={{ fontWeight: 400, color: "var(--color-text-3)", marginLeft: 8, fontSize: 12 }}>{m.detail}</span>}
          </div>
          {(m.steps || []).map((s, j) => (
            <div key={j} style={{ display: "flex", gap: 6, alignItems: "baseline", marginTop: 3, fontSize: 12, flexWrap: "wrap" }}>
              <span style={{ width: 7, height: 7, borderRadius: 4, flex: "none", background: KIND_COLOR[s.kind] || KIND_COLOR.info, transform: "translateY(-1px)" }} />
              <Text style={{ fontSize: 12 }}>{s.text}</Text>
              {s.kind === "tip" && <Tag size="small" color="purple">suggestion</Tag>}
              {(s.kind === "warn" || s.kind === "tip") && s.fix && onFix && FIX_LABELS[s.fix] && (
                <Button size="mini" type="outline" status={s.kind === "warn" ? "warning" : "default"} onClick={() => onFix(s)}>
                  {FIX_LABELS[s.fix]} →
                </Button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Pane({ title, active, style, bodyStyle, children }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", border: active ? "1px solid rgb(var(--arcoblue-5))" : "1px solid var(--color-border-2)",
      boxShadow: active ? "0 0 0 2px rgba(var(--arcoblue-5), .15)" : "none",
      borderRadius: 8, overflow: "hidden", background: "var(--color-bg-2)", minHeight: 0, ...style,
    }}>
      <div style={{ padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "var(--color-text-3)", borderBottom: "1px solid var(--color-border-2)", background: "var(--color-fill-1)", letterSpacing: ".02em", textTransform: "uppercase" }}>
        {title}
      </div>
      <div style={{ flex: 1, minHeight: 560, ...bodyStyle }}>{children}</div>
    </div>
  );
}

// DeviceFrame shows a pane's content as on a desktop (full width) or inside a
// phone-sized frame (390px wide), so their browser and inbox both follow the
// Desktop / Phone switch.
function DeviceFrame({ phone, children }) {
  if (!phone) {
    return <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>{children}</div>;
  }
  return (
    <div style={{ display: "flex", justifyContent: "center", background: "var(--color-fill-2)", padding: 16, height: "100%" }}>
      <div style={{
        width: 390, maxWidth: "100%", minHeight: 520, display: "flex", flexDirection: "column",
        border: "8px solid #1d2129", borderRadius: 28, overflow: "hidden", background: "var(--color-bg-2)",
      }}>
        {children}
      </div>
    </div>
  );
}

// AddressBar is their browser's address bar: where they are (the stand-in
// website, one of our pages, or a site's address). A site that won't be shown
// inside another page leaves the page below blank; the bar says so, and opens
// it in a new tab.
function AddressBar({ address }) {
  const isUrl = /^https?:\/\//.test(address);
  return (
    <div style={{ flex: "none", borderBottom: "1px solid var(--color-border-2)", background: "var(--color-fill-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px" }}>
        <span style={{ display: "flex", gap: 4, flex: "none" }}>
          {[0, 1, 2].map((i) => <span key={i} style={{ width: 8, height: 8, borderRadius: 4, background: "var(--color-border-3)" }} />)}
        </span>
        <div title={address} style={{
          flex: 1, minWidth: 0, padding: "3px 12px", borderRadius: 12, background: "var(--color-bg-2)", fontSize: 12,
          color: "var(--color-text-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {address || "your-website.example (a stand-in for your site)"}
        </div>
        {isUrl && <a href={address} target="_blank" rel="noopener noreferrer" style={{ flex: "none", fontSize: 12 }}>Open in a new tab ↗</a>}
      </div>
      {isUrl && (
        <div style={{ padding: "0 10px 5px", fontSize: 11, color: "var(--color-text-3)" }}>
          Blank below? Some sites won't show inside another page. For them, it opens as usual.
        </div>
      )}
    </div>
  );
}

// TestStrip is the striped "TEST" bar marking Test drive's simulated parts
// (it matches the banner on the stand-in page and the test pages).
function TestStrip({ children }) {
  return (
    <div style={{
      flex: "none", padding: "5px 10px", textAlign: "center", fontSize: 12, fontWeight: 700, letterSpacing: ".02em", color: "#3d2a00",
      background: "repeating-linear-gradient(135deg,#ffd666 0 14px,#ffe58f 14px 28px)", borderBottom: "2px solid #d4a106",
    }}>
      {children}
    </div>
  );
}
